import React, { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import AxuminNavBar from '../../../micro/BrandNavBar/AxuminNavBar';
import TrainingNavBar from '../../../micro/TrainingNavBar/AxuminTrainingNavBar';
import { darkGrey, yellow, white, purple } from '../../../../app/theme';
import Indication from '../../../micro/Indication/AxuminIndication';
import TrainingFooter from '../../../micro/TrainingFooter/TrainingFooter';
import history from '../../../../services/browserHistory';

const useStyles = makeStyles(() => ({
  gradientBox: {
    background: `transparent linear-gradient(180deg, ${white} 0%, ${white} 31%, ${yellow} 100%) 0% 0% no-repeat padding-box;`,
    width: '100%',
    margin: 0,
    padding: '0 40px',
    boxSizing: 'border-box',
  },
  header: {
    fontSize: 48,
    fontWeight: 'bold',
    textAlign: 'center',
    margin: '74px auto 40px',
  },
  subheader: {
    fontSize: 32,
    textAlign: 'center',
    marginBottom: 96,
  },
  recapWrapper: {
    width: '80%',
    margin: 'auto',
    paddingBottom: 78,
  },
  recapTitle: {
    fontSize: 32,
    paddingBottom: 10,
    width: '100%',
    borderBottom: `1px solid ${purple}`,
    marginBottom: 15,
  },
  recapRow: {
    marginBottom: 20,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
  },
  icon: {
    fontSize: 30,
    marginRight: 15,
  },
  recapText: {
    fontSize: 20,
    margin: 0,
  },
  grayBox: {
    backgroundColor: darkGrey,
    width: '100%',
    margin: 0,
    borderTop: `28px solid ${purple}`,
    padding: '55px 40px',
    boxSizing: 'border-box',
  },
  whiteTitle: {
    color: white,
    fontSize: 32,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 28,
  },
  whiteBody: {
    color: white,
    margin: '60px 0 70px',
    textAlign: 'center',
    fontSize: 22,
  },
  whiteSubtitle: {
    maxWidth: '70%',
    margin: 'auto',
    color: white,
    fontSize: 28,
    textAlign: 'center',
    marginTop: 10,
  },
}));

const AxuminTrainingEnd = (): JSX.Element => {
  const classes = useStyles();
  const [moduleDisplay, setModuleDisplay] = useState('');

  useEffect(() => {
    const pathName = history.location.pathname;
    if (pathName.indexOf('interpretation') >= 0) {
      setModuleDisplay('Interpretation');
      document.title = 'Image Interpretation Thank You';
    } else if (pathName.indexOf('acquisition') >= 0) {
      setModuleDisplay('Acquisition');
      document.title = 'Image Acquisition Thank You';
    }
  }, []);

  return (
    <>
      <TrainingNavBar />
      <AxuminNavBar section={`Image ${moduleDisplay}`} />
      <div className={classes.gradientBox}>
        <Typography className={classes.header}>Thank you!</Typography>
        <Typography className={classes.subheader}>
          {`You have successfully completed the Image ${moduleDisplay} Training!`}
        </Typography>
        <div className={classes.recapWrapper}>
          <Typography className={classes.recapTitle}>Learning Objectives Recap</Typography>
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              Describe the mechanism of action and biodistribution of&nbsp;
              {moduleDisplay === 'Acquisition' ? (
                <span>
                  Axumin
                  <sup>®</sup>
                  &nbsp;(fluciclovine F 18)&nbsp;
                </span>
              ) : (
                'Axumin '
              )}
              for diagnostic imaging in patients with suspected prostate cancer recurrence following previous treatment
            </p>
          </div>
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              {`Outline dosing, administration, and patient preparation prior to
              ${moduleDisplay === 'Acquisition' ? ' positron emission tomography (PET) ' : ' PET/CT '}
              imaging in patients with suspected recurrent prostate cancer`}
            </p>
          </div>
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              Prior to utilizing Axumin for patients with suspected recurrent prostate cancer, identify the most
              effective image acquisition and optimal quality imaging display for better interpretation and localization
              of prostate cancer lesions
            </p>
          </div>
          {moduleDisplay === 'Interpretation' && (
            <div className={classes.recapRow}>
              <CheckCircleIcon className={classes.icon} />
              <p className={classes.recapText}>
                Understand the appropriate interpretation criteria based on the location and size of lesions, while
                reviewing applicable image reconstruction planes
              </p>
            </div>
          )}
          {moduleDisplay === 'Interpretation' && (
            <div className={classes.recapRow}>
              <CheckCircleIcon className={classes.icon} />
              <p className={classes.recapText}>
                Describe the expected physiologic uptake mechanism, incidental findings, and normal variants with Axumin
              </p>
            </div>
          )}
          <div className={classes.recapRow}>
            <CheckCircleIcon className={classes.icon} />
            <p className={classes.recapText}>
              Identify the role of Axumin PET/CT imaging by reviewing clinical case studies of suspected recurrent
              prostate cancer
            </p>
          </div>
        </div>
      </div>
      <div className={classes.grayBox}>
        <Typography className={classes.whiteTitle}>Other Avenues to Explore</Typography>
        <Typography className={classes.whiteSubtitle}>
          {`Additional cases, which include a DICOM viewer, are now unlocked within your Image ${moduleDisplay} Training
          home page.`}
        </Typography>
        <Typography className={classes.whiteSubtitle}>
          You will also be able to review all learning check answers, and download an optional statement of completion.
        </Typography>
        <Typography variant="body2" className={classes.whiteBody}>
          All areas of this site are now available to you.
        </Typography>
      </div>
      <Indication />
      <TrainingFooter />
    </>
  );
};

export default AxuminTrainingEnd;
